import { bugsnagError } from 'services/bugsnag';
import * as request from 'utils/request';
import { WAREHOUSE_API_URL } from 'config/endpoints';

// =====
// Utils
// =====
const dataURItoBlob = (dataURI) => {
  if (!dataURI) {
    return;
  }

  let byteString;

  if (dataURI.split(',')[0].indexOf('base64') !== -1) {
    byteString = window.atob(dataURI.split(',')[1]);
  } else {
    byteString = decodeURI(dataURI.split(',')[1]);
  }

  const mimeString = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];

  const content = [];
  for (let i = 0; i < byteString.length; i++) {
    content[i] = byteString.charCodeAt(i);
  }

  return new window.Blob([new Uint8Array(content)], { type: mimeString });
};

// ==============
// Warehouse APIs
// ==============
export const getProductCategories = (spaceId, next = null, results = []) => {
  return request.get(
    next || `${WAREHOUSE_API_URL}/warehouse/api/product-categories?spaceId=${spaceId}`
  ).then(response => {
    if (response && response.error) {
      throw new Error(response.error);
    }

    const categories = results.concat(response.data.results);
    const next = response.data.next;
    return next
      ? getCustomPropsFor(spaceId, next, categories)
      : categories;
  });
};

export const getCustomPropsFor = (spaceId, next = null, results = []) => {
  return request.get(
    next || `${WAREHOUSE_API_URL}/warehouse/api/v1/space_custom_prop?space_id=${spaceId}`
  ).then(response => {
    if (response && response.error) {
      throw new Error(response.error);
    }

    const customProps = results.concat(response.data.results);
    const next = response.data.next;
    return next
      ? getCustomPropsFor(spaceId, next, customProps)
      : customProps;
  });
};

export const createEmptyCustomProp = (customProp) => {
  return request.post(
    `${WAREHOUSE_API_URL}/warehouse/api/v1/custom_prop/create_empty_custom_prop`,
    {
      space_id: customProp.spaceId()
    }
  ).then(response => {
    if (response && response.error) {
      console.log(response);
      throw new Error(response.error);
    }

    return response.data.customPropId;
  });
};

export const updateCustomProp = (customProp) => {
  return request.post(
    `${WAREHOUSE_API_URL}/warehouse/api/v1/custom_prop/${customProp.id()}/submit`,
    {
      customProp: {
        id: customProp.id(),
        height: customProp.height(),
        width: customProp.width(),
        depth: customProp.depth(),
        description: customProp.description(),
        url: customProp.retailerUrl() || '',
        status: customProp.status(),
        type: customProp.type(),
        unit: customProp.units(),
        price: customProp.orderPrice().toString()
      }
    }
  ).then(response => {
    if (response && response.error) {
      console.log(response);
      throw new Error(response.error);
    }

    return Promise.resolve();
  });
};

export const deleteCustomProp = (customPropId, spaceId) => {
  return request.post(
    `${WAREHOUSE_API_URL}/warehouse/api/v1/custom_prop/${spaceId}/submit_props`,
    {
      customProps: [{
        id: customPropId,
        softDelete: true
      }]
    }
  ).then(response => {
    if (response && response.error) {
      bugsnagError(response, { customPropId });
      throw new Error(response.error);
    }

    return Promise.resolve();
  });
};

export const uploadCustomPropImages = (customProp) => {
  const form = new window.FormData();
  form.append('photo', dataURItoBlob(customProp.frontImage()));
  form.append('photo', dataURItoBlob(customProp.sideImage()));

  return request.post(
    `${WAREHOUSE_API_URL}/warehouse/api/v1/custom_prop/${customProp.id()}/upload-image`,
    form
  ).then(response => {
    if (response && response.error) {
      throw response;
    }

    return response;
  });
};

export const submitCustomProps = (orderProps, orderData) => {
  const spaceId = orderProps[0].spaceId();
  const formattedProps = orderProps.map(prop => {
    return {
      id: prop.id(),
      status: prop.status(),
      softDelete: false,
      orderNumber: orderData.orderNumber
    };
  });
  const formattedOrderData = { ...orderData, qty: orderProps.length };

  return request.post(
    `${WAREHOUSE_API_URL}/warehouse/api/v1/custom_prop/${spaceId}/submit_props`,
    {
      customProps: formattedProps,
      orderData: formattedOrderData
    }
  ).then(response => {
    if (response && response.error) {
      console.log(response);
      throw new Error(response.error);
    }

    return Promise.resolve();
  });
};

/**
 * Update current cart with optional promo code.
 */
const toOrderItem = (qty, servicePackage) => {
  return {
    packageId: servicePackage.id,
    qty,
    subtotal: parseFloat(servicePackage.price) * qty,
    type: servicePackage.type,
    extra: { package: servicePackage }
  };
};

export const updateCart = ({ billingInfo = {}, orderProps, paidPropPackage, freePropPackage, promoCode }) => {
  let params = {
    code: promoCode,
    orderItems: [],
    billingInfo
  };

  const paidPropCnt = orderProps.filter(prop => prop.isCustomRender() && prop.orderPrice() > 0).length;
  if (paidPropCnt > 0) {
    params.orderItems.push(toOrderItem(paidPropCnt, paidPropPackage));
  }

  const freePropCnt = orderProps.filter(prop => prop.isFindAlternative() || prop.orderPrice() <= 0).length;
  if (freePropCnt > 0) {
    params.orderItems.push(toOrderItem(freePropCnt, freePropPackage));
  }

  const addlHeaders = { Accept: 'application/json; version=v3' };
  return request.post(`${WAREHOUSE_API_URL}/api/payment/shopping_cart/current`, params, false, () => {}, addlHeaders)
    .then(response => {
      if (response && response.error) {
        const reason = (response.data && response.data.reason) || 'Cart update failed';
        throw new Error(reason);
      }

      const { displayedInfo, orderItems, packagesCoupon } = response.data.packages;
      return { priceData: displayedInfo, orderItems, packagesCoupon, billingInfo };
    });
};

/**
 * Place order with provided order params.
 *
 * @returns Promise that:
 *  resolve with response on success
 *  rejects with error on failure
 */
export const placeOrder = params => {
  const addlHeaders = { Accept: 'application/json; version=v3' };
  const raw = false;
  return request.post(`${WAREHOUSE_API_URL}/api/payment/place_order`, params, raw, () => {}, addlHeaders)
    .then(response => {
      if (response && response.error) {
        throw response;
      }
      return response;
    });
};

export const getStyleboardInfoFor = ({ spaceId }) => {
  return request.get(`${WAREHOUSE_API_URL}/warehouse/api/styleboardinfo?space_id=${spaceId}&ppo=1`)
    .then(response => {
      if (response && response.error) {
        throw response;
      }

      const styleboardInfo = (response.data && response.data.results && response.data.results[0]) || {};
      return styleboardInfo;
    });
};

export const notifyCustomFurnitureStepCompleteFor = ({ spaceId, styleboardInfoId }) => {
  const params = { custom_furniture: true, space_id: spaceId };
  return request.patch(`${WAREHOUSE_API_URL}/warehouse/api/styleboardinfo/${styleboardInfoId}?ppo=1`, params)
    .then(response => {
      if (response && response.error) {
        throw response;
      }
    });
};

// ===========
// Stripe APIs
// ===========
/**
 * Sets the Stripe publishable key for transactions.
 * Requires window.Stripe exists on global window object.
 */
export const setStripePublishibleKey = token => window.Stripe.setPublishableKey(token);

/**
 * Create a stripe payment token for a customer order.
 * Requires window.Stripe exists on global window object
 * with publishable key already set.
 *
 * @returns a promise that:
 *  resolves with Stripe response on success.
 *  rejects with Stripe error on fail.
 */
export const createStripePaymentToken = ({
  orderTotal,
  cardNumber,
  cvc,
  expireMonth,
  expireYear,
  nameOnCard,
  billingAddress,
  billingCity,
  billingState,
  billingZip,
  billingCountry
}) => {
  return new Promise((resolve, reject) => {
    return window.Stripe.card.createToken(
      {
        number: cardNumber,
        cvc,
        exp_month: expireMonth,
        exp_year: expireYear,
        name: nameOnCard,
        address_line1: billingAddress,
        address_city: billingCity,
        address_state: billingState,
        address_zip: billingZip,
        address_country: billingCountry
      },
      orderTotal,
      (status, response) => {
        if (!response.error) {
          return resolve(response);
        }
        return reject(response.error);
      }
    );
  });
};
